import axios from 'axios';
import { getFullHttpUrl } from '../firebase.config';
import authHeaders from './authHeaders';

// EV-242: card and PayPal payments are confirmed by the server. The page still
// takes the payment (Stripe, PayPal's buttons), then sends its ID to
// payments-confirm, which reads it from Stripe/PayPal, checks it was for this
// order, quote or invoice and for its price, and marks it paid (and turns a
// quote into its order). Pay later / the team's billing terms go through
// payments-placeOnAccount. The page no longer writes "paid" itself.

// Until the rules stop the browser from writing payments (EV-241 stage 3):
// when the server can't be reached or fails (network, 5xx), the page marks
// the payment itself as it did before, so nobody is left without their order
// if the new functions have a problem. Answers from the server (4xx) are
// never worked around. Set to false together with stage 3.
export const PAYMENT_LEGACY_FALLBACK = true;

const call = async (name, data) => {
  const res = await axios.post(getFullHttpUrl(`payments-${name}`), data, { headers: await authHeaders() });
  return res.data;
};

// What is paid on /make-payment/:item/:id ("orderInvoice" is an order).
export const payKind = (item) => (item === 'quote' ? 'quote' : item === 'invoice' ? 'invoice' : 'order');

// Stripe PaymentIntent IDs start with pi_; PayPal order IDs are letters and digits.
export const providerOf = (paymentId) => (String(paymentId || '').startsWith('pi_') ? 'stripe' : 'paypal');

// { kind, id, provider, paymentId, discountCode?, shippingInfo? }
// -> { status: 'confirmed' | 'already-confirmed', orderId?, orderNumber? }
export const confirmPaid = ({ kind, id, provider, paymentId, discountCode, shippingInfo }) => call('confirm', {
  kind,
  id,
  provider: provider || providerOf(paymentId),
  paymentId,
  ...(discountCode ? { discountCode } : {}),
  ...(shippingInfo !== undefined ? { shippingInfo } : {}),
});

// { kind: 'order' | 'quote', id, discountCode?, shippingInfo?, clientEmail? }
// -> { status: 'placed', mode: 'pay-later' | 'auto-billing', orderId, orderNumber }
// Refused with "pay-later-paused" while the placer has an invoice 45+ days
// unpaid (functions Invoices/payLater.js).
export const PAY_LATER_PAUSED = "Pay later is paused: you have an invoice unpaid for more than 45 days. Please pay it, or pay this order now with a card or PayPal.";
export const placeOnAccount = ({ kind, id, discountCode, shippingInfo, clientEmail }) => call('placeOnAccount', {
  kind,
  id,
  ...(discountCode ? { discountCode } : {}),
  ...(shippingInfo !== undefined ? { shippingInfo } : {}),
  ...(clientEmail ? { clientEmail } : {}),
});

// The server's answer when it refused (e.g. "already-placed",
// "payment-needs-review", "price-mismatch"), or null when there was no
// answer or the server failed.
export const paymentErrorCode = (error) => {
  const res = error && error.response;
  if (!res || res.status >= 500) {
    return null;
  }
  // A function that isn't deployed (wrong deploy order) answers a 404 page,
  // not one of the server's codes: treat it as "no answer".
  if (res.status === 404 && !(typeof res.data === 'string' && /^[a-z-]{3,40}$/.test(res.data))) {
    return null;
  }
  if (typeof res.data === 'string' && res.data) {
    return res.data;
  }
  return (res.data && res.data.error) || `http-${res.status}`;
};

// Whether to fall back to the old browser write (see PAYMENT_LEGACY_FALLBACK).
export const shouldFallBack = (error) => PAYMENT_LEGACY_FALLBACK && paymentErrorCode(error) === null;
